import {Component, OnInit} from '@angular/core';
import {ShardHttpService} from "../x-http-requests/shard-http.service";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {ShardDto} from "../../models/admin_owner/shard/shard-dto";
import {finalize} from "rxjs";
import {CompanyDto} from "../../models/admin_owner/company/company-dto";
import {CompanyHttpService} from "../x-http-requests/company-http.service";
import {AppRoutes} from "../../enums/routes";
import {PermissionService} from "../../_auth/permission.service";
import {IntegrationProvider} from "../../models/integrations/integration-provider";
import {FiskalyHttpService} from "../../_modules/owner/_services/fiskaly-http.service";
import {GlobalAlertService} from "../../_services/global-alert.service";
import {AlertLevel} from "../../enums/alert-level";
import {OwnerRoutes} from "../../_modules/owner/_models/_enums/owner-routes";
import {describeAdministrationError} from "../../_shared/errors/administration-errors";
import {CommonModule} from "@angular/common";
import {LoadingComponent} from "../../_modules/x-components/loading/loading.component";
import {DropdownComponent} from "../../_ui/components/controls/dropdown/dropdown.component";
import {DropdownItemComponent} from "../../_ui/components/controls/dropdown-item/dropdown-item.component";

@Component({
  selector: 'app-shard',
  templateUrl: './shard.component.html',
  imports: [
    CommonModule,
    LoadingComponent,
    RouterLink,
    DropdownComponent,
    DropdownItemComponent
  ],
  styleUrls: ['./shard.component.scss']
})
export class OwnerShardComponent implements OnInit {
  shards?: ShardDto[];
  filtered_shards?: ShardDto[];
  companies?: CompanyDto[];
  company?: CompanyDto;
  searchText: string = "";
  name: string = "";
  company_filter: string = "";
  // Toolbar state: status filter + sort key, both applied client-side in applyFilters().
  activeFilter: 'all' | 'active' | 'inactive' = 'all';
  sortKey: 'name' | 'newest' | 'modified' = 'name';
  showFilters: boolean = false; // filter/sort toolbar is collapsed by default

  loading: boolean = false;
  protected readonly IntegrationProvider = IntegrationProvider;
  /**
   * (#631) The per-row „Rechtliches" shortcut: `/owner/legal?shardId=…`. Exposed as template constants
   * rather than a hand-written literal so a route rename cannot leave a dead link behind — this row is the
   * shortest way from "which location is this" to its fiscal setup, and the page takes the shard as a deep
   * link precisely so the list can hand it over.
   */
  protected readonly AppRoutes = AppRoutes;
  protected readonly OwnerRoutes = OwnerRoutes;

  constructor(private shardHttpService: ShardHttpService,
              private companyHttpService: CompanyHttpService,
              private route: ActivatedRoute, private router: Router,
              public PermissionService: PermissionService,
              private fiskalyHttpService: FiskalyHttpService,
              private gas: GlobalAlertService) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  redirectToSettings(id: string) {
    this.router.navigate([`${AppRoutes.OwnerModule}/${OwnerRoutes.ShardEdit}`], {queryParams: {id: id}}).then();
  }

  AssignCopy() {
    this.applyFilters();
  }

  FilterItems(value: string) {
    this.searchText = value;
    this.applyFilters();
  }

  setActiveFilter(value: 'all' | 'active' | 'inactive') {
    this.activeFilter = value;
    this.applyFilters();
  }

  setSort(value: 'name' | 'newest' | 'modified') {
    this.sortKey = value;
    this.applyFilters();
  }

  /** Recomputes filtered_shards from the search text, active-state filter and sort key. */
  private applyFilters() {
    if (!this.shards) {
      this.filtered_shards = undefined;
      return;
    }
    const query = this.searchText.trim().toLowerCase();
    let list = this.shards.filter(s => !query || s.name.toLowerCase().includes(query));
    if (this.activeFilter === 'active') list = list.filter(s => s.active);
    else if (this.activeFilter === 'inactive') list = list.filter(s => !s.active);
    this.filtered_shards = list.sort((a, b) => {
      switch (this.sortKey) {
        case 'newest':
          return new Date(b.created).getTime() - new Date(a.created).getTime();
        case 'modified':
          return new Date(b.modified).getTime() - new Date(a.modified).getTime();
        default:
          return a.name.localeCompare(b.name);
      }
    });
  }

  LoadData() {
    this.shardHttpService.list(this.company_filter).subscribe(x => {
      this.shards = x;
      this.AssignCopy();
    }, error => console.error(error));
    if (this.company_filter != "") {
      this.companyHttpService.get(this.company_filter).subscribe(x => {
        this.companies = [x];
        this.company = x;
      }, error => console.error(error));
    } else {
      this.companyHttpService.list().subscribe(x => {
        this.companies = x;
        if (this.companies.length > 0)
          this.company = this.companies[0];
      }, error => console.error(error));
    }
  }

  RemoveShard(shard: ShardDto): void {
    let confirmation = confirm("Are you sure you want to delete the following shard:\n" + shard.name + "\n" + shard.id + "\nOwned by " + shard.company.name);
    if (!confirmation) return;
    this.shardHttpService.delete(shard.id).subscribe(x => {
      this.shards!.splice(this.shards!.indexOf(shard), 1);
      this.FilterItems(this.searchText);
    }, error => {
      console.error(error);
      // `ShardService` registers no error codes either, so this sentence is the entire message — and it names
      // the server's two real rules rather than inviting a retry that cannot change the answer.
      this.fail(error, "Der Standort konnte nicht gelöscht werden. "
        + "Ihr eigener Standort und geschützte Standorte bleiben bestehen.");
    })
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.company_filter = params.company == undefined ? "" : params.company;
    })
    this.LoadData();
  }

  hasIntegrationReference(shard: ShardDto, provider: IntegrationProvider): boolean {
    return shard.integrationReferences.some(x => x.integrationProvider == provider && x.externalId != undefined);
  }

  /**
   * Surfaces a refusal as a German sentence through the global alert banner.
   *
   * Replaces `ErrorBannerComponent`, which rendered `Error · {{ statusText }}`. Every failure this screen can
   * see is a bare status — `ShardService` registers no `ErrorCatalog` codes — so the per-call-site sentence IS
   * the message here.
   */
  private fail(err: unknown, fallback: string): void {
    this.gas.createAndShow("Fehler", describeAdministrationError(err, fallback), AlertLevel.error, 8000);
  }

  fiskalyRegister(shard: ShardDto) {
    // Guard re-entry: Fiskaly provisioning (UpsertShard) is NOT safe to run concurrently for the same shard — two
    // in-flight calls race on the api-key create and the loser fails with E_API_KEY_ALREADY_EXISTS (409). A single
    // user action can dispatch more than once (a rapid second click; the dropdown item re-emitting), so ignore a
    // call while one is already running. `loading` is cleared in `finalize` so the guard releases on success or error.
    if (this.loading) return;
    this.loading = true;
    this.fiskalyHttpService.upsertShard(shard.id)
      .withLoadingIndicator()
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: s => {
          this.gas.createAndShow("Erfolg", "Der Standort ist bei Fiskaly registriert.", AlertLevel.success);
          this.shards?.splice(this.shards?.indexOf(shard), 1, s);
          this.FilterItems(this.searchText);
        },
        error: err => {
          console.error(err);
          this.fail(err, "Die Fiskaly-Registrierung ist fehlgeschlagen. Bitte versuchen Sie es erneut.");
        }
      })
  }
}
